import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { ProfileAvatar } from "@/components/ui/ProfileAvatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { socialService } from "@/services/socialService";
import { articleShareService } from "@/services/articleShareService";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { T } from "@/i18n/T";
import { useT } from "@/i18n/useLocale";

interface ShareArticleModalProps {
  articleId: string;
  articleTitle: string;
  isOpen: boolean;
  onClose: () => void;
}

const ShareArticleModal = ({ articleId, articleTitle, isOpen, onClose }: ShareArticleModalProps) => {
  const t = useT();
  const [selectedFollowers, setSelectedFollowers] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [isSharing, setIsSharing] = useState(false);

  const { data: following, isLoading } = useQuery({
    queryKey: ['following'],
    queryFn: () => socialService.getFollowing(),
    enabled: isOpen,
  });

  const handleToggleFollower = (followerId: string) => {
    setSelectedFollowers(prev => 
      prev.includes(followerId) 
        ? prev.filter(id => id !== followerId)
        : [...prev, followerId]
    );
  };

  const handleShare = async () => {
    if (selectedFollowers.length === 0) {
      toast.error("Please select at least one person to share with");
      return;
    }

    setIsSharing(true);
    const success = await articleShareService.shareArticle(
      articleId,
      selectedFollowers,
      message.trim() || undefined
    );

    setIsSharing(false);

    if (success) {
      toast.success(`Shared with ${selectedFollowers.length} ${selectedFollowers.length === 1 ? 'person' : 'people'}`);
      setSelectedFollowers([]);
      setMessage("");
      onClose();
    } else {
      toast.error("Failed to share article");
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle><T k="auto.share-article-a4bd" /></DialogTitle>
          <DialogDescription className="line-clamp-2">
            {articleTitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              <T k="auto.add-a-message-optional-1896" />
            </label>
            <Textarea
              placeholder={t("auto.why-are-you-sharing-this-1535")}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              maxLength={500}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              <T k="auto.share-with-f27e" />
            </label>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : following && following.length > 0 ? (
              <ScrollArea className="h-48 border rounded-md p-2">
                <div className="space-y-2">
                  {following.map((follow) => {
                    const user = follow.following;
                    return (
                      <div
                        key={user.id}
                        className="flex items-center space-x-3 p-2 rounded-lg hover:bg-accent/50 cursor-pointer"
                        onClick={() => handleToggleFollower(user.id)}
                      >
                        <Checkbox
                          checked={selectedFollowers.includes(user.id)}
                          onCheckedChange={() => handleToggleFollower(user.id)}
                        />
                        <ProfileAvatar userId={user.id} avatarUrl={user.avatar_url} background={user.avatar_background} name={user.full_name || user.username} size="xs" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {user.full_name || user.username || 'Unknown User'}
                          </p>
                          {user.username && (
                            <p className="text-xs text-muted-foreground truncate">
                              @{user.username}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p className="text-sm"><T k="auto.you-re-not-following-anyone-yet-04bb" /></p>
                <p className="text-xs mt-1"><T k="auto.follow-people-to-share-articles-with-the-2376" /></p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} disabled={isSharing}>
              <T k="auto.cancel-ea47" />
            </Button>
            <Button 
              onClick={handleShare} 
              disabled={isSharing || selectedFollowers.length === 0}
            >
              {isSharing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Share
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareArticleModal;
